import {
  Heading,
  SimpleGrid,
  Accordion,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
} from '@chakra-ui/react';
import { useTranslation } from 'next-i18next';
import { RefObject } from 'react';
import { ListingProperty, ListingProps } from '@/types/listing';
import { usePropertyTabs } from '../PropertyTabs';
import { DetailItem, InfoPanel } from './common';
import { usePropertyInfo } from '@/lib/propertyInfo';
import { ComputedDataSection } from '@/lib/propertyInfo/types';

const RenderData = ({ property }: { property?: ListingProperty }) => {
  const { t } = useTranslation(['generated_pdfs', 'backoffice']);
  const { processed: propertyInfo } = usePropertyInfo({
    processor: ({ sections, getComputedSection }) => {
      // Re-create the sections record, but with computed
      // items based on our properties
      return Object.entries(sections).reduce<
        Record<string, ComputedDataSection | undefined>
      >((agg, [key, section]) => {
        if (!property) {
          return agg;
        }

        const computedSection = getComputedSection(section, property);

        return {
          ...agg,
          // if section has no values at all, we set the
          // section value to undefined and don't render it
          [key]: computedSection.hasValues ? computedSection : undefined,
        };
      }, {});
    },
  });

  if (!property || !propertyInfo) {
    return null;
  }

  return (
    <>
      <Accordion allowMultiple>
        <InfoPanel
          sections={[
            propertyInfo.propertyBasicInfo,
            propertyInfo.propertyGroundInfo,
            propertyInfo.propertyWaterAreaInfo,
          ]}
        />
        <InfoPanel
          sections={[
            propertyInfo.propertyLandUsePlanningInfo,
            propertyInfo.propertyWaterPipeInfo,
            propertyInfo.propertySewerInfo,
            propertyInfo.propertyConnectionsInfo,
          ]}
        />
        <InfoPanel sections={[propertyInfo.propertyCostsInfo]} />
        <InfoPanel sections={[propertyInfo.propertyDebtsAndMortgagesInfo]} />
        <InfoPanel sections={[propertyInfo.propertyExtraInformationInfo]} />
        <InfoPanel sections={[propertyInfo.propertyDrivingInstructionsInfo]} />
        <InfoPanel sections={[propertyInfo.propertyEnvironmentAndGroundInfo]} />
        <InfoPanel sections={[propertyInfo.propertyForestDamageInfo]} />
      </Accordion>

      {(property.has_buildings || property.other_buildings.length > 0) && (
        <>
          <Heading size="md">{t('buildings', 'Rakennukset')}</Heading>
          <Accordion allowMultiple>
            {property.has_buildings && (
              <>
                <InfoPanel
                  sections={[
                    propertyInfo.buildingBasicInfo,
                    propertyInfo.buildingKitchenInfo,
                    propertyInfo.buildingBathroomInfo,
                    propertyInfo.buildingSurfacesInfo,
                    propertyInfo.buildingOtherInventoryInfo,
                  ]}
                />
                <InfoPanel
                  sections={[propertyInfo.buildingOtherEquipmentInfo]}
                />
                <InfoPanel
                  sections={[propertyInfo.buildingDamagesAndRenovationsInfo]}
                />
                <InfoPanel
                  sections={[propertyInfo.buildingElectricityWaterInfo]}
                />
                <InfoPanel sections={[propertyInfo.buildingOtherCostsInfo]} />
                <InfoPanel
                  sections={[propertyInfo.buildingNearbyServicesInfo]}
                />
              </>
            )}

            {property.other_buildings.length > 0 && (
              // Other buildings require a bit of special treatment since they're
              // an array of name-value tuples
              <InfoPanel title={t('other_buildings', 'Muut rakennukset')}>
                {property.other_buildings.map(({ id, type, description }) => (
                  <DetailItem
                    key={id}
                    item={{
                      name: 'other_building',
                      label: t(`backoffice:building_type_${type}`),
                      value: description,
                    }}
                  />
                ))}
              </InfoPanel>
            )}
          </Accordion>
        </>
      )}
    </>
  );
};

const ListingDetailsTable = ({
  listing,
  sectionRef,
}: Pick<ListingProps, 'listing'> & {
  sectionRef: RefObject<HTMLDivElement>;
}) => {
  const { tabsProps, tabListProps, tabPanelsProps, tabPanelProps } =
    usePropertyTabs({
      sectionRef,
    });

  if (!listing.properties?.length) {
    return null;
  }

  if (listing.properties.length === 1) {
    return (
      <SimpleGrid columns={1} spacingX={5} spacingY={7}>
        <RenderData property={listing.properties[0]} />
      </SimpleGrid>
    );
  }

  return (
    <>
      <Tabs {...tabsProps} gridColumn="aside !important">
        <TabList {...tabListProps} />
      </Tabs>
      <Tabs {...tabsProps}>
        <TabPanels {...tabPanelsProps}>
          {listing.properties.map((property) => (
            <TabPanel {...tabPanelProps} key={property.id}>
              <SimpleGrid columns={1} spacingX={5} spacingY={7}>
                <RenderData property={property} />
              </SimpleGrid>
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </>
  );
};

export default ListingDetailsTable;
